import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

import type { Form, FormField } from '../../interfaces/form.interface';
import type {
  DropdownData,
  FilteredValue,
  RepeatableComputeContext,
  RepeatableGroupConfiguration,
  RepeatableSubField,
  RepeatableSubFieldContext,
} from '../../interfaces/input.interface';
import { InputType } from '../../interfaces/input.interface';

type RowValue = Record<string, unknown>;

type PendingDropdownItem = {
  rowIndex: number;
  logicalKey: string;
  rawIds: string[];
  sf: RepeatableSubField;
  isMulti: boolean;
};

const NULLISH_STRING_TOKENS = new Set<string>([
  'null',
  'undefined',
  'nan',
]);


const TIPO_ABONO_ORGANICO_LABELS: Record<string, string> = {
  EB: 'Estiércol de bovino',
  EO: 'Estiércol de ovino',
  EP: 'Estiércol de porcino',
  PP: 'Purines de porcino',
  G: 'Gallinaza',
  L: 'Lodos de depuradora',
  C: 'Compost de RSU',
  O: 'Otros',
};

const MATERIAL_FERTILIZANTE_LABELS: Record<string, string> = {
  '1': 'Estiercol líquido de aves',
  '2': 'Estiércol líquido de bovino',
  '3': 'Estiércol líquido de porcino',
  '4': 'Estiércol sólido de aves',
  '5': 'Estiércol sólido de bovino',
  '6': 'Estiércol sólido de caprino',
  '7': 'Estiércol sólido de equino',
  '8': 'Estiércol sólido de ovino',
  '9': 'Inhibidores',
  '10': 'Otros tipos de estiércol sólido: conejo, visón, zorro…',
  '11': 'Productos fertilizantes bioestimulantes',
  '12': 'Productos fertilizantes enmiendas calizas',
  '13': 'Productos fertilizantes enmiendas orgánicas',
  '14': 'Productos fertilizantes inorgánicos',
  '15': 'Productos fertilizantes orgánicos',
  '16': 'Productos fertilizantes organominerales',
  '17': 'Productos fertilizantes otras enmiendas',
  '18': 'Residuos: Materiales sin empleo en la elaboración de fertilizante',
  '19': 'Residuos: Subproductos de origen animal no aptos para consumo humano',
  '20': 'Residuos: compost de alperujo',
  '21': 'Residuos: alperujo desecado',
  '22': 'Residuos: Lodos EDAR',
  '23': 'Residuos: Lodos calizos del proceso Kraft',
};

@Component({
  selector: 'app-repeatable-group',
  standalone: false,
  templateUrl: './repeatable-group.component.html',
  styleUrls: ['./repeatable-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepeatableGroupComponent<T> implements OnInit, OnDestroy {
  @Input({ required: true }) public field: FormField<T> | null = null;
  @Input({ required: true }) public model: FormGroup | null = null;
  @Input({ required: true }) public form: Form<T> | null = null;

  @Input() public isEditable = true;

  /** Enable detailed console diagnostics */
  @Input() public debug = false;

  public readonly INPUT = InputType;

  public rowsForm: FormGroup = new FormGroup({});
  public rowsArray: FormArray<FormGroup> = new FormArray<FormGroup>([]);

  public infoDialogVisible = false;
  public infoDialogRowIndex: number | null = null;

  private readonly destroyed$ = new Subject<void>();
  private readonly modelBindingsStop$ = new Subject<void>();

  /** Avoid infinite loops between model->rows and rows->model */
  private isInternalSync = false;

  /** Prevent writing empty rows into model before server patch arrives */
  private suspendSyncToModel = true;

  /** Pending IDs to resolve when dropdown options are not ready */
  private pendingDropdown: PendingDropdownItem[] = [];
  private isHydratingFromModel = false;
  private pendingResolveTimer: number | null = null;
  private pendingResolveAttempt = 0;
  private readonly skipNextModelHydrationControls: Set<string> = new Set<string>();

  private get cfg(): RepeatableGroupConfiguration {
    const cfg = this.field?.input?.repeatableGroupConfiguration;

    if (!cfg) {
      this.debugWarn('Repeatable group configuration is missing. Rendering empty safely.');
      return {
        rows: 1,
        addButtonLabel: 'Add',
        bindings: {},
        serialization: { separator: ';' },
        subFields: [],
      };
    }

    return cfg;
  }

  public ngOnInit(): void {
    this.debugLog('ngOnInit() start', {
      isEditable: this.isEditable,
      bindings: this.cfg.bindings,
      serialization: this.cfg.serialization,
      subFields: (this.cfg.subFields ?? []).map((s) => s.key),
    });

    this.bootstrapFormSafe();

    // Important: listen for parent patchValue AFTER we bootstrap rows
    this.bindModelHydrationPipeline();

    this.hydrateFromModel();
    this.bindRecomputePipeline();

    this.debugLog('ngOnInit() done');
  }

  public ngOnDestroy(): void {
    this.debugLog('ngOnDestroy()');

    if (this.pendingResolveTimer != null) {
      clearTimeout(this.pendingResolveTimer);
      this.pendingResolveTimer = null;
    }

    this.modelBindingsStop$.next();
    this.modelBindingsStop$.complete();

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && !changes['model'].firstChange) {
      this.debugWarn('model reference changed -> rebind + rehydrate');
      this.bindModelHydrationPipeline();
      this.hydrateFromModel();
    }

    if (changes['field'] && !changes['field'].firstChange) {
      this.debugWarn('field changed -> rehydrate');
      this.hydrateFromModel();
    }
  }
  
  // -----------------------------
  // UI helpers
  // -----------------------------

  public visibleSubFields(): RepeatableSubField[] {
    const subFields = this.cfg.subFields ?? [];

    return subFields.filter((sf) => this.safeIsVisible(sf));
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public get currentCount(): number {
    return this.rowsArray?.length ?? 0;
  }

  public get maxRows(): number {
    const max = Number(this.cfg.rows ?? 1);

    return Number.isFinite(max) && max > 0 ? max : 1;
  }

  public canAddRow(): boolean {
    return this.isEditable && this.currentCount < this.maxRows;
  }

  public canDeleteRow(): boolean {
    return this.isEditable && this.currentCount > 1;
  }

  public addRow(): void {
    if (!this.canAddRow()) return;

    this.debugLog('addRow() before', { len: this.rowsArray.length });

    this.rowsArray.push(this.createRowGroupSafe());

    // If user starts editing before model is "ready", allow sync now.
    this.enableSyncIfUserEditing();

    this.recomputeAllAndSync('addRow');

    this.debugLog('addRow() after', { len: this.rowsArray.length });
  }

  public deleteRow(rowIndex: number): void {
    if (!this.isEditable) return;
    if (rowIndex < 0 || rowIndex >= this.currentCount) return;

    this.debugLog('deleteRow()', { rowIndex, beforeLen: this.rowsArray.length });

    this.rowsArray.removeAt(rowIndex);

    if (this.rowsArray.length === 0) {
      this.rowsArray.push(this.createRowGroupSafe());
    }

    this.enableSyncIfUserEditing();
    this.recomputeAllAndSync('deleteRow');

    this.debugLog('deleteRow() done', { afterLen: this.rowsArray.length });
  }

  /** Fix for ONLY_TEXT where 0 is valid value */
  public onlyTextCellText(sf: RepeatableSubField, rowIndex: number): string {
    const ctrl = this.rowsArray.at(rowIndex)?.get(sf.key);
    const raw = ctrl?.value;

    if (raw === null || raw === undefined) return '0,00';

    const s = String(raw).trim();

    if (!s || this.isNullishStringToken(s)) return '0,00';

    return s;
  }

  public readOnlyCellText(sf: RepeatableSubField, rowIndex: number): string {
    const ctrl = this.rowsArray.at(rowIndex)?.get(sf.key);
    const value = ctrl?.value;

    if (value === null || value === undefined) {
        return sf.input?.type === InputType.ONLY_TEXT ? '0,00' : '-';
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();

        if (!trimmed || this.isNullishStringToken(trimmed)) {
            return sf.input?.type === InputType.ONLY_TEXT ? '0,00' : '-';
        }

        if (this.isInfoLabelValue(sf, trimmed, rowIndex)) {
            return this.normalizeLabelText(trimmed) || '-';
        }
    }

    switch (sf.input?.type) {
        case InputType.DROPDOWN:
        case InputType.DROPDOWN_SEARCH:
            return this.resolveDropdownLabel(sf, value, rowIndex) ?? '-';

        case InputType.MULTISELECT:
        case InputType.MULTISELECT_SEARCH:
            return this.resolveMultiLabels(sf, value, rowIndex) ?? '-';

        default:
            return String(value);
    }
}
  public getNumericInputClasses(sf: RepeatableSubField, rowIndex: number): Record<string, boolean> {
    if (sf.key !== 'dosisCuaderno') {
      return {};
    }

    return {
      'dose-input': true,
      'dose-input-valid': this.getDoseRecommendationStatus(rowIndex) === 'valid',
      'dose-input-invalid': this.getDoseRecommendationStatus(rowIndex) === 'invalid',
    };
  }

  public shouldShowDoseRecommendation(rowIndex: number): boolean {
    const agent = this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'agente');
    const uso = this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'uso');

    if (!agent) {
      return false;
    }

    const usoLabel = String(uso?.['uso'] ?? '').trim();
    const agentLabel = String(agent?.['agente'] ?? '').trim();

    if (agentLabel === '') {
      return false;
    }

    return usoLabel !== '-';
  }

  public getDoseRecommendationText(rowIndex: number): string {
    const range = this.getDoseRange(rowIndex);

    if (!range) {
      return '';
    }

    const minText = this.formatDoseNumber(range.min);
    const unitText = range.unit !== '' ? ` ${range.unit}` : '';

    if (range.max !== null && range.max > 0) {
      return `${minText} - ${this.formatDoseNumber(range.max)}${unitText}`;
    }

    return `${minText}${unitText}`;
  }

  public getDoseRecommendationStatusText(rowIndex: number): string {
    switch (this.getDoseRecommendationStatus(rowIndex)) {
      case 'valid':
        return 'Dentro de rango';

      case 'invalid':
        return 'Fuera de rango';

      default:
        return '';
    }
  }

  public getDoseRecommendationStatusClass(rowIndex: number): string {
    switch (this.getDoseRecommendationStatus(rowIndex)) {
      case 'valid':
        return 'dose-recommendation-valid';

      case 'invalid':
        return 'dose-recommendation-invalid';

      default:
        return 'dose-recommendation-neutral';
    }
  }

  public getPlazoSeguridadText(rowIndex: number): string {
    const agent = this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'agente');
    const raw = String(agent?.['plazo_seguridad'] ?? '').trim();

    return raw;
  }

  public getIntervalosText(rowIndex: number): string {
    const agent = this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'agente');
    const raw = String(agent?.['intervalos'] ?? '').trim();

    return raw;
  }

  public getMaxAplicacionesText(rowIndex: number): string {
    const agent = this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'agente');
    const raw = String(agent?.['n_aplicaciones'] ?? '').trim();

    return raw;
  }

  public getVolCaldoText(rowIndex: number): string {
    const agent = this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'agente');
    const raw = String(agent?.['vol_caldo'] ?? '').trim();

    return raw;
  }

  private getDoseRecommendationStatus(rowIndex: number): 'valid' | 'invalid' | 'unknown' {
    const range = this.getDoseRange(rowIndex);

    if (!range) {
      return 'unknown';
    }

    const row = this.getRowSnapshot(rowIndex);
    const doseValue = this.parseUnknownNumber(row['dosisCuaderno']);

    if (doseValue === null) {
      return 'unknown';
    }

    if (range.max !== null && range.max > 0) {
      return doseValue >= range.min && doseValue <= range.max ? 'valid' : 'invalid';
    }

    return doseValue >= range.min ? 'valid' : 'invalid';
  }

  private getDoseRange(rowIndex: number): { min: number; max: number | null; unit: string } | null {
    const agent = this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'agente');

    if (!agent) {
      return null;
    }

    const min = this.firstFiniteNumber([
      agent['minDosis'],
      agent['dosis_min'],
      agent['dosis1'],
    ]);

    if (min === null || min <= 0) {
      return null;
    }

    const max = this.firstFiniteNumber([
      agent['maxDosis'],
      agent['dosis_max'],
      agent['dosis2'],
    ]);

    const unit = this.normalizeDoseUnit(
      agent['unidad_medida']
      ?? this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'producto')?.['unidades']
      ?? ''
    );

    return {
      min,
      max: max !== null && max > 0 ? max : null,
      unit,
    };
  }

  private getSelectedEntity<T extends Record<string, unknown>>(rowIndex: number, key: string): T | null {
    const rowGroup = this.rowsArray.at(rowIndex);
    const control = rowGroup?.get(key);

    if (!control) {
      return null;
    }

    const value = control.value;

    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      return value as T;
    }

    const sf = (this.cfg.subFields ?? []).find((item) => item.key === key);

    if (!sf) {
      return null;
    }

    const resolved = this.mapIdToDropdownValue(value, sf, rowIndex);

    if (resolved != null && typeof resolved === 'object' && !Array.isArray(resolved)) {
      return resolved as T;
    }

    return null;
  }

  private parseUnknownNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const text = String(value ?? '').trim();

    if (text === '' || this.isNullishStringToken(text)) {
      return null;
    }

    return this.parseFlexibleNumber(text);
  }

  private firstFiniteNumber(values: unknown[]): number | null {
    for (const value of values) {
      const parsed = this.parseUnknownNumber(value);

      if (parsed !== null) {
        return parsed;
      }
    }

    return null;
  }

  private formatDoseNumber(value: number): string {
    return value.toFixed(2).replace('.', ',');
  }

  private normalizeDoseUnit(value: unknown): string {
    const unit = String(value ?? '').trim().toLowerCase();

    switch (unit) {
      case 'kg':
      case 'kilo':
      case 'kilos':
      case 'kilogramo':
      case 'kilogramos':
      case 'kg/ha':
        return 'kg/ha';

      case 'l':
      case 'litro':
      case 'litros':
      case 'l/ha':
        return 'L/ha';

      default:
        return String(value ?? '').trim();
    }
  }

  public canShowProductInfo(rowIndex: number): boolean {
    return this.getSelectedProduct(rowIndex) !== null;
  }

  public canShowRowActions(rowIndex: number): boolean {
    return this.isEditable || this.canShowProductInfo(rowIndex);
  }

  public openProductInfoDialog(rowIndex: number): void {
    if (!this.canShowProductInfo(rowIndex)) {
      return;
    }

    this.infoDialogRowIndex = rowIndex;
    this.infoDialogVisible = true;
  }

  public closeProductInfoDialog(): void {
    this.infoDialogVisible = false;
    this.infoDialogRowIndex = null;
  }

  public getInfoDialogHeaderText(): string {
    const prefix = this.isActiveInfoFertilizer() ? 'Información Fertilizante' : 'Información Producto';

    return `${prefix} ${this.getInfoDialogProductName()}`.trim();
  }

  public getInfoDialogProductName(): string {
    return this.getProductDisplayName(this.getActiveInfoProduct());
  }

  public isActiveInfoFertilizer(): boolean {
    const product = this.getActiveInfoProduct();
    const tipo = String(product?.['tipo'] ?? '').trim().toLowerCase();

    return tipo === 'fertilizante';
  }

  public getInfoDialogFechaCaducidadText(): string {
    return this.readFirstNonEmptyString(this.getActiveInfoProduct(), ['fecha_caducidad']) || '-';
  }

  public getInfoDialogMateriaActivaText(): string {
    return this.readFirstNonEmptyString(this.getActiveInfoProduct(), ['composicion', 'materia_activa']) || '-';
  }

  public getInfoDialogCondicionesUsoText(): string {
    const product = this.getActiveInfoProduct();
    const nestedProduct = this.readObjectRecord(product?.['producto']);

    return (
      this.readFirstNonEmptyString(product, ['condiciones_uso'])
      || this.readFirstNonEmptyString(nestedProduct, ['condiciones_uso'])
      || '-'
    );
  }

  public getInfoDialogUsoText(): string {
    return this.readFirstNonEmptyString(this.getActiveInfoUso(), ['uso']) || '-';
  }

  public getInfoDialogAgenteText(): string {
    return this.readFirstNonEmptyString(this.getActiveInfoAgente(), ['agente']) || '-';
  }

  public getInfoDialogDosisMinText(): string {
    const range = this.getDoseRange(this.infoDialogRowIndex ?? -1);

    if (!range) {
      return '-';
    }

    return this.formatDoseNumber(range.min);
  }

  public getInfoDialogDosisMaxText(): string {
    const range = this.getDoseRange(this.infoDialogRowIndex ?? -1);

    if (!range || range.max === null) {
      return '-';
    }

    return this.formatDoseNumber(range.max);
  }

  public getInfoDialogDosisAplicadaText(): string {
    if (this.infoDialogRowIndex === null || this.infoDialogRowIndex < 0) {
      return '-';
    }

    const row = this.getRowSnapshot(this.infoDialogRowIndex);
    const dosisAplicada = this.parseUnknownNumber(row['dosisCuaderno']);

    if (dosisAplicada === null) {
      return '-';
    }

    return this.formatDoseNumber(dosisAplicada);
  }

  public getInfoDialogUnidadText(): string {
    const range = this.getDoseRange(this.infoDialogRowIndex ?? -1);

    if (range?.unit) {
      return range.unit;
    }

    return (
      this.readFirstNonEmptyString(this.getActiveInfoAgente(), ['unidad_medida'])
      || this.readFirstNonEmptyString(this.getActiveInfoProduct(), ['unidades'])
      || '-'
    );
  }

  public getInfoDialogVolCaldoText(): string {
    return this.getRowMetricText(this.infoDialogRowIndex, 'vol_caldo');
  }

  public getInfoDialogFormaEpocaText(): string {
    return (
      this.readFirstNonEmptyString(this.getActiveInfoUso(), ['forma_epoca'])
      || this.readFirstNonEmptyString(this.getActiveInfoAgente(), ['forma_epoca'])
      || '-'
    );
  }

  public getInfoDialogMaxAplicacionesText(): string {
    return this.getRowMetricText(this.infoDialogRowIndex, 'n_aplicaciones');
  }

  public getInfoDialogIntervalosText(): string {
    return this.getRowMetricText(this.infoDialogRowIndex, 'intervalos');
  }

  public getInfoDialogPlazoSeguridadText(): string {
    return this.getRowMetricText(this.infoDialogRowIndex, 'plazo_seguridad');
  }

  public getInfoDialogFertiDescripcionText(): string {
    return this.readProductString(['descripcion']) || '-';
  }

  public getInfoDialogFertiComposicionText(): string {
    return this.readProductString(['composicion']) || '-';
  }

  public getInfoDialogFertiUnidadesText(): string {
    return this.readProductString(['unidades']) || '-';
  }

  public getInfoDialogFertiEnvaseText(): string {
    return this.readProductString(['envase']) || '-';
  }

  public getInfoDialogFertiTipoAbonoOrganicoText(): string {
    const raw = this.readProductString(['tipo_abono_organico']);

    if (raw === '') {
      return '-';
    }

    return TIPO_ABONO_ORGANICO_LABELS[raw] ?? raw;
  }

  public getInfoDialogFertiAbonoConInhibidorText(): string {
    const value = this.getActiveInfoProduct()?.['abono_con_inhibidor'];

    if (value === null || value === undefined || this.isNullishStringToken(String(value))) {
      return '-';
    }

    return this.coerceBoolean(value) ? 'Sí' : 'No';
  }

  public getInfoDialogFertiMaterialFertiText(): string {
    const raw = this.readProductString(['material_ferti']);

    if (raw === '') {
      return '-';
    }

    return MATERIAL_FERTILIZANTE_LABELS[raw] ?? raw;
  }

  public getInfoDialogFertiFieldText(field: string): string {
    const product = this.getActiveInfoProduct();

    if (!product) {
      return '-';
    }

    const value = product[field];

    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? String(value).replace('.', ',') : '-';
    }

    const text = String(value).trim();

    if (text === '' || this.isNullishStringToken(text)) {
      return '-';
    }

    return text;
  }

  public getInfoDialogFertiDosisAplicadaText(): string {
    return this.getInfoDialogDosisAplicadaText();
  }

  public getInfoDialogFertiUnidadAplicadaText(): string {
    const unit = this.readFirstNonEmptyString(this.getActiveInfoProduct(), ['unidades'])
      || this.getInfoDialogUnidadText();

    return unit || '-';
  }

  private getActiveInfoProduct(): Record<string, unknown> | null {
    if (this.infoDialogRowIndex === null || this.infoDialogRowIndex < 0) {
      return null;
    }

    return this.getSelectedProduct(this.infoDialogRowIndex);
  }

  private getActiveInfoUso(): Record<string, unknown> | null {
    if (this.infoDialogRowIndex === null || this.infoDialogRowIndex < 0) {
      return null;
    }

    return this.getSelectedUso(this.infoDialogRowIndex);
  }

  private getActiveInfoAgente(): Record<string, unknown> | null {
    if (this.infoDialogRowIndex === null || this.infoDialogRowIndex < 0) {
      return null;
    }

    return this.getSelectedAgente(this.infoDialogRowIndex);
  }

  private getSelectedProduct(rowIndex: number): Record<string, unknown> | null {
    return this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'producto');
  }

  private getSelectedUso(rowIndex: number): Record<string, unknown> | null {
    return this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'uso');
  }

  private getSelectedAgente(rowIndex: number): Record<string, unknown> | null {
    return this.getSelectedEntity<Record<string, unknown>>(rowIndex, 'agente');
  }

  private getProductDisplayName(product: Record<string, unknown> | null): string {
    return (
      this.readFirstNonEmptyString(product, ['nombre_comercial', 'nombre'])
      || '-'
    );
  }

  private getRowMetricText(rowIndex: number | null, key: string): string {
    if (rowIndex === null || rowIndex < 0) {
      return '-';
    }

    return this.readFirstNonEmptyString(this.getSelectedAgente(rowIndex), [key]) || '-';
  }

  private readProductString(keys: string[]): string {
    return this.readFirstNonEmptyString(this.getActiveInfoProduct(), keys);
  }

  private coerceBoolean(value: unknown): boolean {
    if (value === true || value === 1) {
      return true;
    }

    const normalized = String(value ?? '').trim().toLowerCase();

    return normalized === '1' || normalized === 'true' || normalized === 'sí' || normalized === 'si';
  }

  private readFirstNonEmptyString(
    source: Record<string, unknown> | null | undefined,
    keys: string[],
  ): string {
    if (!source) {
      return '';
    }

    for (const key of keys) {
      const value = source[key];
      const text = String(value ?? '').trim();

      if (text !== '' && !this.isNullishStringToken(text)) {
        return text;
      }
    }

    return '';
  }

  private readObjectRecord(value: unknown): Record<string, unknown> | null {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    return value as Record<string, unknown>;
  }



  public debugDump(): void {
    if (!this.debug) return;

    this.debugLog('debugDump()', {
      isEditable: this.isEditable,
      maxRows: this.maxRows,
      suspendSyncToModel: this.suspendSyncToModel,
      rows: this.getRowsSnapshot(),
      pendingDropdown: this.pendingDropdown,
      modelValue: this.model?.getRawValue?.() ?? null,
    });
  }

  // -----------------------------
  // Model -> rows hydration (critical)
  // -----------------------------

  /**
   * Rehydrate whenever parent patches the bound model controls.
   * Also: determine when model is "ready" to allow rows->model sync.
   */
  private bindModelHydrationPipeline(): void {
    if (!this.model) {
      this.debugWarn('bindModelHydrationPipeline(): model missing.');
      return;
    }

    const entries = Object.entries(this.cfg.bindings ?? {});
    if (entries.length === 0) return;

    // stop previous if any
    this.modelBindingsStop$.next();

    for (const [logicalKey, targetControlName] of entries) {
      const ctrl = this.model.get(targetControlName);

      if (!ctrl) {
        this.debugWarn('Model control missing for binding.', { logicalKey, targetControlName });
        continue;
      }

      ctrl.valueChanges
        .pipe(auditTime(0), takeUntil(this.destroyed$), takeUntil(this.modelBindingsStop$))
        .subscribe((v: unknown) => {
          if (this.skipNextModelHydrationControls.delete(targetControlName)) {
            this.debugLog('Ignored internal model echo.', {
              targetControlName,
              value: v,
            });
            return;
          }

          if (this.isInternalSync || this.isHydratingFromModel) return;

          if (this.suspendSyncToModel && this.hasNonEmptyValue(v)) {
            this.suspendSyncToModel = false;

            this.debugLog('Model became ready -> enabling sync to model.', {
              targetControlName,
              value: v,
            });
          }

          this.debugLog('External model change -> rehydrateFromModel()', {
            targetControlName,
            value: v,
          });

          this.hydrateFromModel();
        });
    }

    // If model already has values at init, allow sync immediately
    if (this.isAnyBindingNonEmpty(entries)) {
      this.suspendSyncToModel = false;
      this.debugLog('Model already ready at init -> enabling sync to model.');
    }
  }

  private isAnyBindingNonEmpty(entries: Array<[string, string]>): boolean {
    if (!this.model) return false;

    return entries.some(([, ctrlName]) => this.hasNonEmptyValue(this.model?.get(ctrlName)?.value));
  }

  private hasNonEmptyValue(v: unknown): boolean {
    if (v === null || v === undefined) return false;

    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s !== '' && !this.isNullishStringToken(s);
    }

    if (Array.isArray(v)) return v.length > 0;

    return true;
  }

  // -----------------------------
  // Form creation
  // -----------------------------

  private bootstrapFormSafe(): void {
    this.rowsArray = new FormArray<FormGroup>([]);
    this.rowsArray.push(this.createRowGroupSafe());

    this.rowsForm = new FormGroup({
      rows: this.rowsArray,
    });
  }

  private createRowGroupSafe(): FormGroup {
    const group: Record<string, AbstractControl> = {};
    const subFields = this.cfg.subFields ?? [];

    for (const sf of subFields) {
      // Must accept objects for dropdowns, arrays for multi, etc.
      group[sf.key] = new FormControl<unknown>(null, { nonNullable: false });
    }

    return new FormGroup(group);
  }

  // -----------------------------
  // rows -> recompute/sync pipeline
  // -----------------------------

  private bindRecomputePipeline(): void {
    this.rowsArray.valueChanges
      .pipe(auditTime(0), takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.isInternalSync) return;

        // user is editing => allow sync even if server hasn't patched yet
        this.enableSyncIfUserEditing();

        this.debugLog('rowsArray.valueChanges -> recomputeAllAndSync()', this.getRowsSnapshot());
        this.recomputeAllAndSync('rowsArray.valueChanges');
      });
  }

  private enableSyncIfUserEditing(): void {
    if (!this.isEditable) return;

    if (this.suspendSyncToModel) {
      this.suspendSyncToModel = false;
      this.debugWarn('Sync to model enabled due to user editing before model became ready.');
    }
  }

  private recomputeAllAndSync(source: string): void {
    this.recomputeAll(source);
    this.syncRowsToModel(source);
  }

  private recomputeAll(source: string): void {
    const subFields = this.cfg.subFields ?? [];
    const rowsForCompute = this.getRowsSnapshotAsStrings();

    for (let rowIndex = 0; rowIndex < this.rowsArray.length; rowIndex++) {
      const rowGroup = this.rowsArray.at(rowIndex);

      for (const sf of subFields) {
        if (typeof sf.computeValue !== 'function') continue;

        const ctx: RepeatableComputeContext = {
          rowIndex,
          rows: rowsForCompute,
          model: this.model ?? new FormGroup({}),
        };

        let computed: string;

        try {
          computed = sf.computeValue(ctx);
        } catch (err: unknown) {
          this.debugWarn('computeValue() threw. Skipping patch.', { source, rowIndex, key: sf.key, err });
          continue;
        }

        const control = rowGroup.get(sf.key);
        if (!control) continue;

        let row = rowsForCompute[rowIndex];
        if (!this.areEqualLoose(control.value, computed) && row) {
          control.patchValue(computed, { emitEvent: false });
          row[sf.key] = String(computed);
        }
      }
    }
  }

  private syncRowsToModel(source: string): void {
    if (this.isHydratingFromModel) {
      this.debugWarn('syncRowsToModel(): skipped during hydrateFromModel().', {
        source,
        pendingCount: this.pendingDropdown.length,
      });
      return;
    }

    if (this.pendingDropdown.length > 0) {
      this.debugWarn('syncRowsToModel(): skipped because dropdown resolution is still pending.', {
        source,
        pendingCount: this.pendingDropdown.length,
      });
      return;
    }

    if (!this.model) {
      this.debugWarn('syncRowsToModel(): model missing, skipping.', { source });
      return;
    }

    if (this.suspendSyncToModel) {
      this.debugWarn('syncRowsToModel(): skipped (model not ready).', { source });
      return;
    }

    const { bindings, serialization } = this.cfg;
    const sep = serialization?.separator ?? ';';

    const rows = this.getRowsSnapshot();
    const subFields = this.cfg.subFields ?? [];

    this.isInternalSync = true;

    try {
      for (const [logicalKey, targetControlName] of Object.entries(bindings ?? {})) {
        const sf = subFields.find((x) => x.key === logicalKey);

        const joined = rows
          .map((r) => this.serializeCellValue(r[logicalKey], sf))
          .join(sep);

        const ctrl = this.model.get(targetControlName);

        if (ctrl) {
          const prev = String(ctrl.value ?? '');

          if (prev !== joined) {
            this.skipNextModelHydrationControls.add(targetControlName);
            ctrl.patchValue(joined, { emitEvent: true });

            this.debugLog('syncRowsToModel: patched existing model control.', {
              source,
              logicalKey,
              targetControlName,
              prev,
              joined,
            });
          }
        } else {
          this.model.addControl(targetControlName, new FormControl(joined));

          this.debugWarn('syncRowsToModel: model control missing; created it.', {
            source,
            logicalKey,
            targetControlName,
            joined,
          });

          this.model.updateValueAndValidity({ emitEvent: false });
        }
      }
    } finally {
      this.isInternalSync = false;
    }
  }

  // -----------------------------
  // Hydrate from model (robust)
  // -----------------------------

  private hydrateFromModel(): void {
    if (!this.model) {
      this.debugWarn('hydrateFromModel(): model missing.');
      return;
    }

    const { bindings, serialization } = this.cfg;
    const sep = serialization?.separator ?? ';';

    const entries = Object.entries(bindings ?? {});
    if (entries.length === 0) {
      this.debugWarn('hydrateFromModel(): No bindings found.');
      return;
    }

    const splitByControl: Record<string, string[]> = {};
    let maxLen = 0;

    for (const [logicalKey, targetControlName] of entries) {
      const raw = this.model.get(targetControlName)?.value;
      const parts = this.splitSerializedKeepEmptiesAndSanitize(raw, sep);

      splitByControl[logicalKey] = parts;
      maxLen = Math.max(maxLen, parts.length);

      this.debugLog('hydrate split', { logicalKey, targetControlName, raw, parts, partsLen: parts.length });
    }

    const desiredRows = Math.min(Math.max(maxLen, 1), this.maxRows);

    while (this.rowsArray.length < desiredRows) {
      this.rowsArray.push(this.createRowGroupSafe());
    }

    while (this.rowsArray.length > desiredRows) {
      this.rowsArray.removeAt(this.rowsArray.length - 1);
    }

    this.pendingDropdown = [];
    this.pendingResolveAttempt = 0;

    const subFields = this.cfg.subFields ?? [];

    this.isHydratingFromModel = true;
    this.isInternalSync = true;

    try {
      for (const [logicalKey] of entries) {
        const sf = subFields.find((f) => f.key === logicalKey);

        if (!sf) {
          this.debugWarn('hydrateFromModel(): subField not found for logicalKey.', { logicalKey });
          continue;
        }

        const values = splitByControl[logicalKey] ?? [];

        for (let rowIndex = 0; rowIndex < this.rowsArray.length; rowIndex++) {
          const row = this.rowsArray.at(rowIndex);
          const ctrl = row.get(logicalKey);

          if (!ctrl) {
            this.debugWarn('hydrate: missing row control.', { rowIndex, logicalKey });
            continue;
          }

          const token = String(values[rowIndex] ?? '').trim();

          if (!token || this.isNullishStringToken(token)) {
            ctrl.patchValue(null, { emitEvent: false });
            continue;
          }

          const type = sf.input?.type;

          const isSelect =
            type === InputType.DROPDOWN ||
            type === InputType.DROPDOWN_SEARCH ||
            type === InputType.MULTISELECT ||
            type === InputType.MULTISELECT_SEARCH;

          if (!this.isEditable && isSelect) {
            const looksLikeLabel = !this.isNumericLike(token);

            if (looksLikeLabel) {
              ctrl.patchValue(token, { emitEvent: false });
              continue;
            }
          }

          if (type === InputType.NUMERIC) {
            const parsed = this.parseFlexibleNumber(token);

            if (parsed === null) {
              this.debugWarn('NUMERIC token not parseable -> input type=number will show blank.', { key: logicalKey, token });
              ctrl.patchValue(null, { emitEvent: false });
            } else {
              ctrl.patchValue(parsed, { emitEvent: false });
            }

            continue;
          }

          if (isSelect) {
            this.hydrateSelectCell(sf, ctrl, rowIndex, logicalKey, token);
            continue;
          }

          ctrl.patchValue(token, { emitEvent: false });
        }
      }
    } finally {
      this.isInternalSync = false;
      this.isHydratingFromModel = false;
    }

    this.recomputeAll('hydrateFromModel');

    if (this.pendingDropdown.length > 0) {
      this.debugWarn('hydrateFromModel(): pendingDropdown (options not ready or ID filtered out).', {
        pendingCount: this.pendingDropdown.length,
        pending: this.pendingDropdown,
      });

      this.scheduleResolvePending();
      return;
    }

    this.syncRowsToModel('hydrateFromModel');
  }

  private hydrateSelectCell(
    sf: RepeatableSubField,
    ctrl: AbstractControl,
    rowIndex: number,
    logicalKey: string,
    token: string,
): void {
    const primaryKey = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const type = sf.input?.type;

    const isMulti =
        type === InputType.MULTISELECT ||
        type === InputType.MULTISELECT_SEARCH;

    if (!isMulti) {
        const selectedObj = this.mapIdToDropdownValue(token, sf, rowIndex);

        if (selectedObj) {
            ctrl.patchValue(selectedObj, { emitEvent: false });
            return;
        }

        ctrl.patchValue(null, { emitEvent: false });
        this.logWhySelectCannotRender(sf, rowIndex, token);
        this.debugWarn('hydrateSelectCell(): unresolved select token.', {
            rowIndex,
            logicalKey,
            token,
            key: sf.key,
            optionsLen: this.dropdownOptions(sf, rowIndex).length,
        });

        this.pendingDropdown.push({
            rowIndex,
            logicalKey,
            rawIds: [token],
            sf,
            isMulti: false,
        });

        return;
    }

    const ids = this.parseIdsFromToken(token, ',', primaryKey);
    const options = this.dropdownOptions(sf, rowIndex);

    const resolved = ids
        .map((id) => options.find((option) => this.getOptionKey(option?.value, primaryKey) === id)?.value ?? null)
        .filter((value): value is unknown => value !== null);

    ctrl.patchValue(resolved, { emitEvent: false });

    const unresolvedIds = ids.filter((id) => !options.some((option) => this.getOptionKey(option?.value, primaryKey) === id));

    if (unresolvedIds.length > 0) {
        this.pendingDropdown.push({
            rowIndex,
            logicalKey,
            rawIds: unresolvedIds,
            sf,
            isMulti: true,
        });
    }
}

  // -----------------------------
  // Pending resolve when options arrive
  // -----------------------------

  private scheduleResolvePending(): void {
    if (this.pendingResolveTimer != null) {
      clearTimeout(this.pendingResolveTimer);
      this.pendingResolveTimer = null;
    }

    this.pendingResolveTimer = window.setTimeout(() => {
      this.tryResolvePending();
    }, 50);

    this.debugLog('scheduleResolvePending() setTimeout 50ms');
  }

  private tryResolvePending(): void {
    if (this.pendingDropdown.length === 0) {
        return;
    }

    const maxAttempts = 200;

    this.pendingResolveAttempt++;

    const stillPending: PendingDropdownItem[] = [];

    for (const item of this.pendingDropdown) {
        const rowGroup = this.rowsArray.at(item.rowIndex);
        const ctrl = rowGroup?.get(item.logicalKey);

        if (!ctrl) {
            this.debugWarn('tryResolvePending(): control missing.', { item });
            continue;
        }

        const primaryKey = item.sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const options = this.dropdownOptions(item.sf, item.rowIndex);

        if (!item.isMulti) {
            const id = item.rawIds[0] ?? '';
            const selectedObj = this.mapIdToDropdownValue(id, item.sf, item.rowIndex);

            if (selectedObj) {
                ctrl.patchValue(selectedObj, { emitEvent: false });
            } else {
                stillPending.push(item);
            }

            continue;
        }

        const current = Array.isArray(ctrl.value) ? (ctrl.value as unknown[]) : [];
        const resolvedNow = item.rawIds
            .map((id) => options.find((option) => this.getOptionKey(option?.value, primaryKey) === id)?.value ?? null)
            .filter((value): value is unknown => value !== null);

        const next = [...current, ...resolvedNow];

        ctrl.patchValue(next, { emitEvent: false });

        const unresolvedIds = item.rawIds.filter((id) => !options.some((option) => this.getOptionKey(option?.value, primaryKey) === id));

        if (unresolvedIds.length > 0) {
            stillPending.push({ ...item, rawIds: unresolvedIds });
        }
    }

    this.pendingDropdown = stillPending;

    this.debugLog('tryResolvePending(): status', {
        attempt: this.pendingResolveAttempt,
        stillPendingCount: stillPending.length,
        stillPending,
        rows: this.getRowsSnapshot(),
        modelValue: this.model?.getRawValue?.() ?? null,
    });

    if (this.pendingDropdown.length > 0 && this.pendingResolveAttempt < maxAttempts) {
        this.scheduleResolvePending();
        return;
    }

    this.recomputeAll('tryResolvePending');

    if (this.pendingDropdown.length > 0) {
        this.debugWarn('tryResolvePending(): unresolved dropdowns remain after max attempts. Keeping model untouched.', {
            pendingCount: this.pendingDropdown.length,
            pending: this.pendingDropdown,
        });
        return;
    }

    this.syncRowsToModel('tryResolvePending');
}

  private getRowSnapshot(rowIndex: number): Record<string, unknown> {
    return (this.rowsArray.at(rowIndex)?.getRawValue() as Record<string, unknown> | undefined) ?? {};
}

private buildSubFieldContext(rowIndex: number): RepeatableSubFieldContext {
    return {
        rowIndex,
        row: this.getRowSnapshot(rowIndex),
        rows: this.getRowsSnapshotAsStrings(),
        model: this.model ?? new FormGroup({}),
    };
}

private getDropdownData(sf: RepeatableSubField, rowIndex: number): DropdownData<any> | undefined {
    if (typeof sf.getData === 'function') {
        try {
            return sf.getData(this.buildSubFieldContext(rowIndex)) as DropdownData<any>;
        } catch (err: unknown) {
            this.debugWarn('getData() threw, falling back to static data.', {
                key: sf.key,
                rowIndex,
                err
            });
        }
    }

    return sf.input?.data as DropdownData<any> | undefined;
}

  // -----------------------------
  // Dropdown / labels utilities
  // -----------------------------

  public dropdownOptions(sf: RepeatableSubField, rowIndex: number): Array<FilteredValue<any>> {
    try {
        const dd = this.getDropdownData(sf, rowIndex);
        const cfg = sf.input?.dropdownConfiguration;

        const filtered = (dd?.filtered ?? []) as Array<FilteredValue<any>>;
        const values = (dd?.values ?? []) as Array<FilteredValue<any>>;

        const filteredHasRealValues = filtered.some((it) => it?.value != null);
        const valuesHasRealValues = values.some((it) => it?.value != null);

        let base: Array<FilteredValue<any>> = [];

        if (cfg?.filteredValues?.length) {
            base = cfg.filteredValues as Array<FilteredValue<any>>;
        } else if (valuesHasRealValues) {
            base = values;
        } else if (filteredHasRealValues) {
            base = filtered;
        } else {
            base = [];
        }

        const filterFn = cfg?.filter as ((it: any) => boolean | undefined) | undefined;

        if (!filterFn) {
            return base;
        }

        return base.filter((it) => {
            try {
                return filterFn(it) !== false;
            } catch (err: unknown) {
                this.debugWarn('dropdownOptions(): filter threw, keeping item by default.', {
                    key: sf.key,
                    rowIndex,
                    err
                });

                return true;
            }
        });
    } catch (err: unknown) {
        this.debugWarn('dropdownOptions(): unexpected error, returning empty list.', {
            key: sf.key,
            rowIndex,
            err
        });

        return [];
    }
}

private mapIdToDropdownValue(idRaw: unknown, sf: RepeatableSubField, rowIndex: number): any | null {
    const id = String(idRaw ?? '').trim();

    if (!id || this.isNullishStringToken(id)) {
        return null;
    }

    const primaryKey = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const options = this.dropdownOptions(sf, rowIndex);
    const found = options.find((option) => this.getOptionKey(option?.value, primaryKey) === id);

    if (found) {
        return found.value;
    }

    const fallbackLabel = this.getFallbackRowLabel(sf, rowIndex);

    if (!fallbackLabel) {
        return null;
    }

    const normalizedFallbackLabel = this.normalizeOptionLabel(fallbackLabel);
    const foundByLabel = options.find((option) => {
        const optionLabel = this.normalizeOptionLabel(option?.label ?? '');

        return optionLabel !== '' && optionLabel === normalizedFallbackLabel;
    });

    if (foundByLabel) {
        this.debugLog('mapIdToDropdownValue(): resolved by fallback label.', {
            key: sf.key,
            rowIndex,
            id,
            fallbackLabel,
            resolvedId: this.getOptionKey(foundByLabel.value, primaryKey),
        });

        return foundByLabel.value;
    }

    return null;
}

private logWhySelectCannotRender(sf: RepeatableSubField, rowIndex: number, rawId: string): void {
    if (!this.debug) {
        return;
    }

    const primaryKey = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const options = this.dropdownOptions(sf, rowIndex);
    const dd = this.getDropdownData(sf, rowIndex);

    const baseValuesLen = Array.isArray(dd?.values) ? dd.values.length : 0;
    const baseFilteredLen = Array.isArray(dd?.filtered) ? dd.filtered.length : 0;

    const reason =
        options.length === 0
            ? 'Options list is empty.'
            : options.some((option) => this.getOptionKey(option?.value, primaryKey) === rawId)
                ? 'ID exists in options but UI still blank.'
                : 'ID not found in options.';

    this.debugWarn('Select cannot display value.', {
        rowIndex,
        key: sf.key,
        rawId,
        primaryKey,
        optionsLen: options.length,
        baseValuesLen,
        baseFilteredLen,
        reason,
    });
}


private getFallbackRowLabel(sf: RepeatableSubField, rowIndex: number): string {
    const rowGroup = this.rowsArray.at(rowIndex);
    const labelControlKey = this.getFallbackLabelControlKey(sf);

    if (!rowGroup || !labelControlKey) {
        return '';
    }

    return String(rowGroup.get(labelControlKey)?.value ?? '').trim();
}

private getFallbackLabelControlKey(sf: RepeatableSubField): string | null {
    switch (sf.key) {
        case 'uso':
            return 'usoNombre';

        case 'agente':
            return 'agenteNombre';

        default:
            return null;
    }
}

private normalizeOptionLabel(value: unknown): string {
    return String(value ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
}

  // -----------------------------
  // Read-only: labels in info mode
  // -----------------------------

  private isInfoLabelValue(sf: RepeatableSubField, raw: unknown, rowIndex: number): boolean {
    if (this.isEditable) {
        return false;
    }

    const type = sf.input?.type;
    const isSelect =
        type === InputType.DROPDOWN ||
        type === InputType.DROPDOWN_SEARCH ||
        type === InputType.MULTISELECT ||
        type === InputType.MULTISELECT_SEARCH;

    if (!isSelect || raw === null || raw === undefined || typeof raw === 'object') {
        return false;
    }

    const stringValue = String(raw).trim();

    if (!stringValue || this.isNullishStringToken(stringValue)) {
        return false;
    }

    const parts = stringValue.split(';').map((part) => part.trim()).filter((part) => part !== '');

    if (parts.length === 0) {
        return false;
    }

    const allNumericLike = parts.every((part) => this.isNumericLike(part));

    if (allNumericLike) {
        return false;
    }

    const primaryKey = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const options = this.dropdownOptions(sf, rowIndex);

    if (options.length > 0) {
        const anyPartIsKnownId = parts.some((part) =>
            options.some((option) => this.getOptionKey(option?.value, primaryKey) === part)
        );

        if (anyPartIsKnownId) {
            return false;
        }
    }

    return true;
}

private resolveDropdownLabel(sf: RepeatableSubField, raw: unknown, rowIndex: number): string | null {
    const primaryKey = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const ids = this.parseIdsFromToken(raw, ';', primaryKey);

    if (ids.length === 0) {
        return null;
    }

    const labels = this.idsToLabels(sf, ids, rowIndex);

    return labels.length ? labels.join(', ') : null;
}


  private normalizeLabelText(raw: unknown): string {
    const s = String(raw ?? '').trim();
    if (!s) return '';

    const parts = s.split(';').map((x) => x.trim()).filter((x) => x !== '');

    return parts.join(', ');
  }

  // -----------------------------
  // Serialize helpers
  // -----------------------------

  private serializeCellValue(value: unknown, sf?: RepeatableSubField): string {
    if (value === null || value === undefined) return '';

    // Keep info-mode labels string as-is
    if (!this.isEditable && typeof value === 'string') {
      const s = value.trim();

      if (!s || this.isNullishStringToken(s)) return '';

      if (s.includes(';')) return s;

      return s;
    }

    // numeric -> format for backend (comma decimal)
    if (typeof value === 'number') {
      return this.formatNumberForModel(value);
    }

    const pk = sf?.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

    // multi -> "id1,id2"
    if (Array.isArray(value)) {
      const ids = value
        .map((x) => this.normalizeToId(x, pk))
        .filter((x): x is string => !!x);

      return ids.join(',');
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;

      if (obj['value'] && typeof obj['value'] === 'object') {
        const inner = obj['value'] as Record<string, unknown>;
        const id = inner[pk] ?? inner['id'];
        return this.valueToStringSafe(id);
      }

      const id = obj[pk] ?? obj['id'];
      return this.valueToStringSafe(id);
    }

    return this.valueToStringSafe(value);
  }

  private valueToStringSafe(value: unknown): string {
    if (value === null || value === undefined) return '';

    const s = String(value).trim();

    if (!s || this.isNullishStringToken(s)) return '';

    return s;
  }

  // -----------------------------
  // Read-only label resolution
  // -----------------------------

private resolveMultiLabels(sf: RepeatableSubField, raw: unknown, rowIndex: number): string | null {
    const primaryKey = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const ids = this.parseIdsFromToken(raw, ';', primaryKey);

    if (ids.length === 0) {
        return null;
    }

    const labels = this.idsToLabels(sf, ids, rowIndex);

    return labels.length ? labels.join(', ') : null;
}


  private parseIdsFromToken(raw: unknown, itemSeparator: string, pk: string): string[] {
    if (raw === null || raw === undefined) return [];

    if (Array.isArray(raw)) {
      return raw
        .map((x) => this.normalizeToId(x, pk))
        .filter((x): x is string => !!x);
    }

    if (typeof raw === 'string' || typeof raw === 'number') {
      const s = String(raw).trim();

      if (!s || this.isNullishStringToken(s)) return [];

      return s
        .split(itemSeparator)
        .map((x) => x.trim())
        .filter((x) => x !== '' && !this.isNullishStringToken(x));
    }

    const one = this.normalizeToId(raw, pk);

    return one ? [one] : [];
  }

private idsToLabels(sf: RepeatableSubField, ids: string[], rowIndex: number): string[] {
    const primaryKey = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const options = this.dropdownOptions(sf, rowIndex);

    return ids
        .map((id) => options.find((option) => this.getOptionKey(option?.value, primaryKey) === id)?.label ?? '')
        .filter((label) => label.trim() !== '');
}

  private normalizeToId(raw: unknown, pk: string): string | null {
    if (raw === null || raw === undefined) return null;

    if (typeof raw === 'string' || typeof raw === 'number') {
      const s = String(raw).trim();

      if (!s || this.isNullishStringToken(s)) return null;

      return s;
    }

    if (typeof raw === 'object') {
      const obj = raw as any;

      if (obj.value && typeof obj.value === 'object') {
        const id = obj.value?.[pk] ?? obj.value?.id;
        return id != null ? String(id).trim() : null;
      }

      const id = obj?.[pk] ?? obj?.id;
      return id != null ? String(id).trim() : null;
    }

    return null;
  }

  // -----------------------------
  // Numeric parsing/formatting
  // -----------------------------

  private parseFlexibleNumber(token: string): number | null {
    const t = token.trim();
    if (!t || this.isNullishStringToken(t)) return null;

    const lastComma = t.lastIndexOf(',');
    const lastDot = t.lastIndexOf('.');

    let normalized = t;

    if (lastComma !== -1 && lastDot !== -1) {
      normalized = lastComma > lastDot
        ? t.replace(/\./g, '').replace(',', '.')
        : t.replace(/,/g, '');
    } else if (lastComma !== -1) {
      normalized = t.replace(',', '.');
    }

    // eslint-disable-next-line no-restricted-globals
    const n = Number(normalized);

    return Number.isFinite(n) ? n : null;
  }

  private formatNumberForModel(n: number): string {
    // 2 decimals like "0,00"
    return n.toFixed(2).replace('.', ',');
  }

  // -----------------------------
  // Visibility safety
  // -----------------------------

  private safeIsVisible(sf: RepeatableSubField): boolean {
    if (typeof sf.isVisible !== 'function') return true;

    try {
      return sf.isVisible() !== false;
    } catch (err: unknown) {
      this.debugWarn('isVisible() threw; showing field by default.', { key: sf.key, err });
      return true;
    }
  }

  // -----------------------------
  // Snapshot utilities
  // -----------------------------

  private getRowsSnapshot(): RowValue[] {
    const rows: RowValue[] = [];

    for (let i = 0; i < this.rowsArray.length; i++) {
      rows.push({ ...(this.rowsArray.at(i).getRawValue() as RowValue) });
    }

    return rows;
  }

  private getRowsSnapshotAsStrings(): Array<Record<string, string>> {
    const rows: Array<Record<string, string>> = [];
    const subFields = this.cfg.subFields ?? [];
    const subFieldMap = new Map<string, RepeatableSubField>(
      subFields.map((sf) => [sf.key, sf])
    );

    for (let i = 0; i < this.rowsArray.length; i++) {
      const raw = this.rowsArray.at(i).getRawValue() as RowValue;
      const out: Record<string, string> = {};

      for (const [k, v] of Object.entries(raw)) {
        const subField = subFieldMap.get(k);

        out[k] = this.serializeCellValue(v, subField);
      }

      rows.push(out);
    }

    return rows;
  }

  private splitSerializedKeepEmptiesAndSanitize(value: unknown, sep: string): string[] {
    return String(value ?? '')
      .split(sep)
      .map((x) => {
        const t = String(x ?? '').trim();

        if (!t || this.isNullishStringToken(t)) return '';

        return t;
      });
  }

  private areEqualLoose(a: unknown, b: unknown): boolean {
    return String(a ?? '') === String(b ?? '');
  }

  private isNumericLike(value: string): boolean {
    const s = value.trim();
    if (!s) return false;

    // eslint-disable-next-line no-restricted-globals
    return !Number.isNaN(Number(s));
  }

  private isNullishStringToken(value: string): boolean {
    return NULLISH_STRING_TOKENS.has(value.trim().toLowerCase());
  }

  private debugLog(message: string, data?: unknown): void {
    if (!this.debug) return;
    // eslint-disable-next-line no-console
    console.log(`[RepeatableGroup] ${message}`, data ?? '');
  }

  private debugWarn(message: string, data?: unknown): void {
    if (!this.debug) return;
    // eslint-disable-next-line no-console
    console.warn(`[RepeatableGroup] ${message}`, data ?? '');
  }

private getOptionKey(optionValue: unknown, primaryKey: string): string {
    if (optionValue == null) {
        return '';
    }

    if (typeof optionValue === 'object') {
        return String((optionValue as Record<string, unknown>)[primaryKey] ?? '');
    }

    return String(optionValue);
}
}
